<template>
  <Teleport to="body">
    <div class="fm-root" @click.self="$emit('close')">
      <div class="fm-box" role="dialog" aria-modal="true">
        <div class="fm-head">
          <span class="fm-title">{{ t('fields.manage') }}</span>
          <button class="fm-x" :aria-label="t('drawer.close')" @click="$emit('close')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="fm-body">
          <!-- Create -->
          <div class="fm-section-label">{{ t('fields.newField') }}</div>
          <div class="fm-create">
            <input v-model="form.label" class="form-control" :placeholder="t('fields.namePlaceholder')" maxlength="80" />
            <select v-model="form.type" class="form-control fm-type">
              <option value="text">{{ t('fields.typeText') }}</option>
              <option value="number">{{ t('fields.typeNumber') }}</option>
              <option value="date">{{ t('fields.typeDate') }}</option>
              <option value="select">{{ t('fields.typeSelect') }}</option>
            </select>
          </div>

          <div v-if="form.type === 'select'" class="fm-options">
            <span v-for="(o, i) in form.options" :key="i" class="fm-opt">
              {{ o }}<button class="fm-opt-x" @click="form.options.splice(i, 1)">×</button>
            </span>
            <div class="fm-opt-add">
              <input v-model="newOption" class="form-control" :placeholder="t('fields.addValue')" @keyup.enter="addOption" />
              <button class="btn btn-ghost btn-sm" :disabled="!newOption.trim()" @click="addOption">+ {{ t('fields.addValueShort') }}</button>
            </div>
          </div>

          <div class="fm-actions">
            <button class="btn btn-ghost btn-sm" :class="{ active: form.scope === 'all' }" @click="create('all')" :disabled="!canCreate || busy">
              {{ t('fields.scopeAll') }}
            </button>
            <button class="btn btn-primary btn-sm" @click="create('project')" :disabled="!canCreate || busy">
              {{ t('fields.scopeThis') }}
            </button>
          </div>

          <!-- Existing -->
          <div class="fm-section-label fm-existing-label">{{ t('fields.existing') }}</div>
          <p v-if="!fields.length" class="fm-empty">{{ t('fields.empty') }}</p>
          <ul v-else class="fm-list">
            <li v-for="f in fields" :key="f.id" class="fm-item">
              <span class="fm-item-name">{{ f.label }}</span>
              <span class="fm-item-type">{{ t('fields.type' + cap(f.type)) }}</span>
              <span v-if="f.project_id === null" class="fm-item-badge">{{ t('fields.globalBadge') }}</span>
              <button class="fm-item-x" :title="t('fields.remove')" @click="remove(f)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { reactive, ref, computed } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'

export default {
  name: 'FieldManagerModal',
  props: {
    projectId:   { type: Number, required: true },
    projectName: { type: String, default: '' },
    fields:      { type: Array,  default: () => [] },
  },
  emits: ['close', 'changed'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const toast = useToast()
    const busy  = ref(false)
    const newOption = ref('')
    const form = reactive({ label: '', type: 'text', options: [], scope: 'project' })

    const canCreate = computed(() =>
      form.label.trim() && (form.type !== 'select' || form.options.length > 0))

    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

    function addOption() {
      const v = newOption.value.trim()
      if (v && !form.options.includes(v)) form.options.push(v)
      newOption.value = ''
    }

    async function create(scope) {
      if (!canCreate.value || busy.value) return
      busy.value = true
      try {
        await api.createProjectField(props.projectId, {
          label: form.label.trim(),
          type: form.type,
          options: form.type === 'select' ? form.options : undefined,
          scope,
        })
        Object.assign(form, { label: '', type: 'text', options: [], scope: 'project' })
        toast.success(t('toast.saved'))
        emit('changed')
      } catch { toast.error(t('toast.saveFailed')) }
      finally { busy.value = false }
    }

    async function remove(f) {
      if (!confirm(t('fields.removeConfirm', { label: f.label }))) return
      try { await api.deleteProjectField(f.id); toast.success(t('toast.saved')); emit('changed') }
      catch { toast.error(t('toast.actionFailed')) }
    }

    return { t, busy, newOption, form, canCreate, cap, addOption, create, remove }
  },
}
</script>

<style scoped>
.fm-root { position: fixed; inset: 0; z-index: 1200; display: grid; place-items: center; background: rgba(16,22,40,.42); padding: 20px; }
.fm-box { width: min(440px, 100%); max-height: 86vh; overflow-y: auto; background: var(--surface); border: 1px solid var(--border); border-radius: 16px; box-shadow: 0 18px 48px rgba(16,22,40,.28); }
.fm-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px; border-bottom: 1px solid var(--border); }
.fm-title { font-size: 16px; font-weight: 700; }
.fm-x { border: none; background: none; cursor: pointer; color: var(--muted); display: inline-flex; padding: 4px; border-radius: 8px; }
.fm-x:hover { color: var(--text); background: var(--hover); }
.fm-body { padding: 16px 18px; }
.fm-section-label { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .03em; margin-bottom: 10px; }
.fm-existing-label { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--border); }
.fm-create { display: flex; gap: 8px; }
.fm-type { width: 130px; flex-shrink: 0; }
.fm-options { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; align-items: center; }
.fm-opt { display: inline-flex; align-items: center; gap: 4px; background: var(--accent-wash); color: var(--accent-ink); border-radius: 999px; padding: 3px 10px; font-size: 13px; }
.fm-opt-x { border: none; background: none; cursor: pointer; color: inherit; font-size: 15px; line-height: 1; }
.fm-opt-add { display: flex; gap: 6px; flex: 1 1 160px; }
.fm-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.btn-ghost.active { border-color: var(--accent); color: var(--accent-ink); }
.fm-empty { font-size: 13px; color: var(--faint); }
.fm-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
.fm-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 10px; }
.fm-item-name { font-size: 14px; font-weight: 600; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.fm-item-type { font-size: 11px; color: var(--muted); }
.fm-item-badge { font-size: 10px; font-weight: 700; color: var(--accent-ink); background: var(--accent-wash); border-radius: 6px; padding: 1px 6px; }
.fm-item-x { border: none; background: none; cursor: pointer; color: var(--muted); display: inline-flex; padding: 3px; border-radius: 6px; }
.fm-item-x:hover { color: #fff; background: var(--danger); }
</style>
